import React, { useState } from 'react';
import Select from 'react-select';
import '../../../../style/CompanyFormTabs.css';

const organisationSizes = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1000-10000'
];

const CompanyFormTabs = ({
  activeTab,
  formData,
  setFormData,
  errors,
  setErrors,
  logoPreview,
  setLogoPreview,
  tabValidation,
  setTabValidation,
  isEditMode,
  // State and City props
  allStates,
  filteredStates,
  selectedState,
  stateSearchTerm,
  allCities,
  filteredCities,
  selectedCity,
  citySearchTerm,
  showStateDropdown,
  showCityDropdown,
  handleStateInputChange,
  handleCityInputChange,
  handleStateSelect,
  handleCitySelect,
  onStateInputBlur,
  onCityInputBlur,
  setShowStateDropdown,
  setShowCityDropdown,
  industries
}) => {

  const AWS = process.env.REACT_APP_AWS_HOST;
  
  function isValidUrl(string) {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;  
    }
  }

  const handleInputChange = (e, isURl = false) => {
    const { name, value, type, checked } = e.target;

    let sanitizedValue = value;

    if (isURl) {
      if (!isValidUrl(value)) {
        setErrors(prev => ({ ...prev, [name]: "Invalid URL" }));
      }
    } else {
      sanitizedValue = value.replace(/[^A-Za-z0-9 @.-]/g, "");
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : sanitizedValue
    }));

    // Clear errors when user starts typing
    if (!isURl && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, companyLogo: 'Logo file size must be less than 5MB' }));
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, companyLogo: 'Only JPEG, JPG, PNG, and GIF files are allowed' }));
        return;
      }

      // Clear any existing errors
      setErrors(prev => ({ ...prev, companyLogo: '' }));

      setFormData(prev => ({
        ...prev,
        companyLogo: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({ ...prev, status }));
  };

  return (
    <div className="company-form-container">
      {/* Company Basic Info */}
      {activeTab === 0 && (
        <>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Company Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.companyName ? 'is-invalid' : ''}`}
                name="companyName"
                value={formData.companyName}
                onChange={(e)=>handleInputChange(e,false)}
                placeholder="Enter company name"
                maxLength={255}
              />
              {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Company Logo</label>
              <input
                type="file"
                className={`form-control ${errors.companyLogo ? 'is-invalid' : ''}`}
                accept="image/*"
                onChange={handleFileChange}
              />
              {errors.companyLogo && <div className="invalid-feedback">{errors.companyLogo}</div>}
              {logoPreview && (
                <div className="mt-3">
                  <img 
                    src={logoPreview.startsWith('uploads') ? `${AWS}${logoPreview}` : logoPreview}
                    alt="Logo Preview" 
                    className="img-thumbnail"
                    style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain' }} 
                  />
                </div>
              )}
              <div className="form-text">
                Upload company logo (Max size: 5MB, Formats: JPEG, JPG, PNG, GIF)
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Company Website <span className="text-danger">*</span>
              </label>
              <input
                type="url"
                className={`form-control ${errors.companyWebsite ? 'is-invalid' : ''}`}
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={(e)=>handleInputChange(e,true)}
                placeholder="https://www.example.com"
              />
              {errors.companyWebsite && <div className="invalid-feedback">{errors.companyWebsite}</div>}
              <div className="form-text">
                Enter the complete URL including http:// or https://
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                Organisation Size <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.organisationSize ? 'is-invalid' : ''}`}
                name="organisationSize"
                value={formData.organisationSize}
                onChange={handleInputChange}
              >
                <option value="">Select organization size</option>
                {organisationSizes.map((size, idx) => (
                  <option key={idx} value={size}>{size} employees</option>
                ))}
              </select>
              {errors.organisationSize && <div className="invalid-feedback">{errors.organisationSize}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Industry <span className="text-danger">*</span>
              </label>
              <Select
                className={`${errors.industry_id ? 'is-invalid' : ''}`}
                classNamePrefix="react-select"
                name="industry_id"
                options={Array.isArray(industries) ? industries.map(ind => ({
                  value: ind.id.toString(),
                  label: ind.title
                })) : []}
                value={Array.isArray(industries) ? {
                  value: formData.industry_id,
                  label: industries.find(ind => ind.id.toString() === formData.industry_id.toString())?.title || 'Select industry'
                } : null}
                onChange={(selectedOption) => {
                  setFormData(prev => ({
                    ...prev,
                    industry_id: selectedOption ? selectedOption.value : ''
                  }));
                  if (errors.industry_id) {
                    setErrors(prev => ({ ...prev, industry_id: "" }));
                  }
                }}
                placeholder="Select industry"
                isSearchable={true}
                isClearable={true}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: errors.industry_id ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#dee2e6'),
                    boxShadow: state.isFocused ? (errors.industry_id ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)') : 'none',
                    '&:hover': {
                      borderColor: errors.industry_id ? '#dc3545' : '#86b7fe'
                    },
                    borderRadius: '0.375rem',
                    padding: '0.1rem'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
              {errors.industry_id && <div className="text-danger mt-1" style={{fontSize: '0.875em'}}>{errors.industry_id}</div>}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Status</label>
            <div className="status-group">
              <div className="d-flex gap-4">
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="statusActive"
                    name="status"
                    checked={formData.status === 1}
                    onChange={() => handleStatusChange(1)}
                  />
                  <label className="form-check-label" htmlFor="statusActive">
                    Active
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="statusInactive"
                    name="status"
                    checked={formData.status === 0}
                    onChange={() => handleStatusChange(0)}
                  />
                  <label className="form-check-label" htmlFor="statusInactive">
                    Inactive
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">
              About Company <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control ${errors.aboutUs ? 'is-invalid' : ''}`}
              name="aboutUs"
              value={formData.aboutUs}
              onChange={handleInputChange}
              rows="4"
              placeholder="Enter company description, mission, vision, and key information..."
              maxLength={1500}
            />
            {errors.aboutUs && <div className="invalid-feedback">{errors.aboutUs}</div>}
            <div className="form-text character-counter">
              {formData.aboutUs.length}/1500 characters
            </div>
          </div>
        </>
      )}

      {/* Address Tab */}
      {activeTab === 1 && (
        <>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Address Line 1 <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.address1 ? 'is-invalid' : ''}`}
                name="address1"
                value={formData.address1}
                onChange={handleInputChange}
                placeholder="Enter street address, building number"
                maxLength={255}
              />
              {errors.address1 && <div className="invalid-feedback">{errors.address1}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-control"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
                placeholder="Enter apartment, suite, unit, floor (optional)"
                maxLength={255}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                State <span className="text-danger">*</span>
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  value={stateSearchTerm}
                  onChange={handleStateInputChange}
                  onFocus={() => setShowStateDropdown(true)}
                  onBlur={onStateInputBlur}
                  className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                  placeholder="Search for a state..."
                />
                {showStateDropdown && filteredStates.length > 0 && (
                  <div className="dropdown-menu show position-absolute w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                    {filteredStates.map(state => (
                      <button
                        key={state.id}
                        type="button"
                        className="dropdown-item"
                        onMouseDown={() => handleStateSelect(state)}
                      >
                        {state.name}
                      </button>
                    ))}
                  </div>
                )}
                {errors.state && <div className="invalid-feedback">{errors.state}</div>}
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                City <span className="text-danger">*</span>
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  value={citySearchTerm}
                  onChange={handleCityInputChange}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={onCityInputBlur}
                  className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                  placeholder={!selectedState ? "Please select a state first" : "Search for a city..."}
                  disabled={!selectedState}
                />
                {showCityDropdown && filteredCities.length > 0 && (
                  <div className="dropdown-menu show position-absolute w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                    {filteredCities.map(city => (
                      <button
                        key={city.id}
                        type="button"
                        className="dropdown-item"
                        onMouseDown={() => handleCitySelect(city)}
                      >
                        {city.name}
                      </button>
                    ))}
                  </div>
                )}
                {errors.city && <div className="invalid-feedback">{errors.city}</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Contact Person Tab */}
      {activeTab === 2 && (
        <>
          <div className="mb-3">
            <label className="form-label">
              Contact Person Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.contactPersonName ? 'is-invalid' : ''}`}
              name="contactPersonName"
              value={formData.contactPersonName}
              onChange={handleInputChange}
              placeholder="Enter full name of contact person"
              maxLength={100}
            />
            {errors.contactPersonName && <div className="invalid-feedback">{errors.contactPersonName}</div>}
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Contact Number <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                className={`form-control ${errors.contactPersonNumber ? 'is-invalid' : ''}`}
                name="contactPersonNumber"
                value={formData.contactPersonNumber}
                onChange={handleInputChange}
                maxLength={10}
                placeholder="Enter 10-digit mobile number"
                pattern="[0-9]{10}"
              />
              {errors.contactPersonNumber && <div className="invalid-feedback">{errors.contactPersonNumber}</div>}
              <div className="form-text">
                Enter 10-digit mobile number without country code
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Contact Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={`form-control ${errors.contactPersonEmail ? 'is-invalid' : ''}`}
                name="contactPersonEmail"
                value={formData.contactPersonEmail}
                onChange={handleInputChange}
                placeholder="Enter email address"
                maxLength={100}
              />
              {errors.contactPersonEmail && <div className="invalid-feedback">{errors.contactPersonEmail}</div>}
              <div className="form-text">
                This email will be used for official communications
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CompanyFormTabs;